import { useRef, useState, useEffect, useMemo, useCallback } from 'react'
import { useRecoilState, useSetRecoilState } from 'recoil'
import clsx from 'clsx'
import { userConfigState, layoutState } from '@/recoil/UserConfig'
import IconSetting from '@/components/icons/IconSetting'
import IconColorSetting from '@/components/icons/IconColorSetting'
import { accountInfoState } from '@/recoil/AccountInfo'
import useOnClickOutside from '@/hooks/useOnClickOutside'
import fetcher from '@/api/fetcher'
import debounce from '@/utils/debounce'
import { getDefaultConfig, saveConfig } from '@/utils/userConfig'
import { getDefaultLayoutConfig } from '@/utils/layout'
import { toggleLocalStorageWatcher } from '@/utils/localStorage'
import { toggleOrderBookAnimation } from '@/utils/toggleClass'
import { LOCAL_STORAGE_CONFIG_KEY } from '@/constants/config'
import './Setting.css'

export default function Setting() {
  const setLayout = useSetRecoilState(layoutState)
  const [userConfig, setUserConfig] = useRecoilState(userConfigState)
  const [accountInfo, setAccountInfo] = useRecoilState(accountInfoState)
  const [isActive, setIsActive] = useState(false)
  const [maintenanceCancelOrder, setMaintenanceCancelOrder] = useState(false)

  const settingRef = useRef()
  useOnClickOutside(settingRef, () => setIsActive(false))

  const debounceUpdateMaintenanceCancelOrderConfig = useMemo(
    () =>
      debounce(
        (currentConfig, newConfig) =>
          currentConfig !== newConfig &&
          fetcher
            .updateAccountMaintenanceConfig({
              maintenance_cancel_order_flag: newConfig
            })
            .then(
              result => result && fetcher.getAccountInfo().then(setAccountInfo)
            ),
        500
      ),
    [setAccountInfo]
  )

  const toggleConfigByKey = useCallback(
    key =>
      setUserConfig(config => {
        const newConfig = { ...config, [key]: !config[key] }
        saveConfig(newConfig)
        return newConfig
      }),
    [setUserConfig]
  )

  const resetLayout = useCallback(
    () => setLayout(getDefaultLayoutConfig().layouts),
    [setLayout]
  )

  useEffect(() => {
    const onStorageChange = ({ key, newValue }) => {
      if (key === LOCAL_STORAGE_CONFIG_KEY) {
        try {
          const userConfig = JSON.parse(newValue) || getDefaultConfig()
          setUserConfig(userConfig)
        } catch (e) {}
      }
    }
    toggleLocalStorageWatcher(onStorageChange)
    return () => toggleLocalStorageWatcher(onStorageChange, false)
  }, [setUserConfig])

  useEffect(() => {
    toggleOrderBookAnimation(userConfig.animation)
  }, [userConfig.animation])

  useEffect(() => {
    if (accountInfo.maintenance_cancel_orders === undefined) {
      return
    }
    setMaintenanceCancelOrder(accountInfo.maintenance_cancel_orders)
  }, [accountInfo.maintenance_cancel_orders])

  useEffect(() => {
    if (accountInfo.maintenance_cancel_orders === undefined) {
      return
    }
    debounceUpdateMaintenanceCancelOrderConfig(
      accountInfo.maintenance_cancel_orders,
      maintenanceCancelOrder
    )
  }, [
    accountInfo.maintenance_cancel_orders,
    debounceUpdateMaintenanceCancelOrderConfig,
    maintenanceCancelOrder
  ])

  return (
    <div ref={settingRef} className="header-icon-button-container noselect">
      <div
        className="header-icon-button"
        onClick={() => setIsActive(active => !active)}
      >
        {isActive ? <IconColorSetting /> : <IconSetting />}
      </div>
      <div className={clsx('setting-list', { active: isActive })}>
        <div
          className="setting-list-row"
          onClick={() => toggleConfigByKey('animation')}
        >
          <div className="setting-label">Animation</div>
          <div
            className={clsx('toggle-switch', {
              active: userConfig.animation
            })}
          />
        </div>
        <div
          className="setting-list-row"
          onClick={() => toggleConfigByKey('ordersOnChart')}
        >
          <div className="setting-label">Orders on chart</div>
          <div
            className={clsx('toggle-switch', {
              active: userConfig.ordersOnChart
            })}
          />
        </div>
        {accountInfo.account_id && (
          <div
            className="tooltip-container setting-list-row"
            onClick={() => setMaintenanceCancelOrder(v => !v)}
          >
            <div className="setting-label">Maintenance cancel</div>
            <div
              className={clsx('toggle-switch', {
                active: maintenanceCancelOrder
              })}
            />
            <div className="tooltip">
              Automatically cancel pending orders during maintenance
            </div>
          </div>
        )}
        <div
          className="setting-list-row"
          onClick={() => toggleConfigByKey('lockLayout')}
        >
          <div className="setting-label">Lock Layout</div>
          <div
            className={clsx('toggle-switch', {
              active: userConfig.lockLayout
            })}
          />
        </div>
        <div className="setting-list-row" onClick={resetLayout}>
          <div className="setting-label">Reset Layout</div>
        </div>
      </div>
    </div>
  )
}
